import { useCallback, useState } from 'react'

import { ReleaseForm, TaggableUser } from '@api'

import { Dialog } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ReleaseFormTabContent } from '@/dialogs/TagOtherCreatorsAndReleaseFormsDialog/ReleaseFormTabContent/ReleaseFormTabContent.tsx'

import type { DialogProps } from '../types'
import { TagUserTabContent } from './TagUserTabContent/TagUserTabContent'
import { useEntitySelection } from './useEntitySelection.ts'

interface Props extends DialogProps {
  onAddTags: (tags: (ReleaseForm | TaggableUser)[]) => void
}

export const TagOtherCreatorsAndReleaseFormsDialog = ({
  isOpen,
  onClose,
  onAddTags,
}: Props) => {
  const [contentType, setContentTypes] = useState<string>('tag-user')
  const [
    selectedCreators,
    handleChangeCreatorSelection,
    resetCreatorSelection,
  ] = useEntitySelection<TaggableUser>()
  const [
    selectedReleaseForms,
    handleChangeFormsSelection,
    resetFormsSelection,
  ] = useEntitySelection<ReleaseForm>()

  const applySelection = useCallback(() => {
    onAddTags([...selectedCreators, ...selectedReleaseForms])
    onClose()
  }, [onClose, onAddTags, selectedCreators, selectedReleaseForms])

  const handleClickResetSelection = useCallback(() => {
    resetFormsSelection()
    resetCreatorSelection()
  }, [resetFormsSelection, resetCreatorSelection])

  return (
    <Dialog
      bigDialog
      show={isOpen}
      onClose={onClose}
      panelClassName="lg:max-w-[700px]"
      childrenClassName="max-w-full overflow-hidden"
      showCloseIcon={false}
    >
      <div className="space-y-6">
        <Dialog.Header>
          <Dialog.Title>Tag Other Creators</Dialog.Title>
        </Dialog.Header>
        <Tabs
          value={contentType}
          onValueChange={setContentTypes}
          className="mt-6 flex w-full flex-col justify-center"
        >
          <TabsList className="bg-secondary-50 dark:border-secondary-400 dark:bg-secondary-600 grid w-full max-w-[420px] grid-cols-2 self-center">
            <TabsTrigger value="tag-user">Tag User</TabsTrigger>
            <TabsTrigger value="release-form">Release Form</TabsTrigger>
          </TabsList>
          <TabsContent
            value="tag-user"
            className="mt-6"
          >
            <TagUserTabContent
              onClose={onClose}
              onClickDone={applySelection}
              selectedReleaseForms={selectedReleaseForms}
              selectedCreators={selectedCreators}
              onChangeCreatorSelection={handleChangeCreatorSelection}
              onResetSelection={handleClickResetSelection}
            />
          </TabsContent>
          <TabsContent
            value="release-form"
            className="mt-6"
          >
            <ReleaseFormTabContent
              onClose={onClose}
              onClickDone={applySelection}
              selectedCreators={selectedCreators}
              selectedReleaseForms={selectedReleaseForms}
              onChangeReleaseFormSelection={handleChangeFormsSelection}
              onResetSelection={handleClickResetSelection}
            />
          </TabsContent>
        </Tabs>
      </div>
    </Dialog>
  )
}
